import React, { useState, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Card } from 'src/@/components/ui/card';
import { Button } from 'src/@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from 'src/@/components/ui/popover';
import { TAREFAS_DESIGN } from './Tarefas.design';

const WEEKDAY_LABELS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

// Máximo de chips visíveis por dia antes de agrupar em "+N mais".
const MAX_VISIBLE_PER_DAY = 3;

const D = TAREFAS_DESIGN.calendar;

function pad2(n) {
    return String(n).padStart(2, '0');
}

function dateKey(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatTime(dateString) {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    // Meia-noite em ponto quase sempre significa "prazo sem horário definido";
    // exibir 00:00 nos chips só adicionaria ruído.
    if (d.getHours() === 0 && d.getMinutes() === 0) return '';
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export default function CalendarView({ tasks, sortedEstagios, onEdit, onNewTask }) {
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

    const doneStageName = sortedEstagios.length > 0 ? sortedEstagios[sortedEstagios.length - 1].nome : null;

    const isTaskDone = (task) => {
        if (doneStageName) return task.status === doneStageName;
        return task.status === 'done' || task.status === 'Concluído';
    };

    const isOverdue = (task) => {
        if (!task.due_date || isTaskDone(task)) return false;
        return new Date(task.due_date) < new Date();
    };

    const stageColor = (task) => {
        const estagio = sortedEstagios.find(e => e.nome === task.status);
        return estagio?.cor || '#9ca3af';
    };

    // Tarefas agrupadas por dia (chave YYYY-MM-DD em horário local), ordenadas
    // por horário dentro do dia para o empilhamento seguir a agenda.
    const { tasksByDay, tasksWithoutDate } = useMemo(() => {
        const byDay = {};
        const withoutDate = [];
        for (const task of tasks) {
            if (!task.due_date) {
                withoutDate.push(task);
                continue;
            }
            const d = new Date(task.due_date);
            if (Number.isNaN(d.getTime())) {
                withoutDate.push(task);
                continue;
            }
            const key = dateKey(d);
            if (!byDay[key]) byDay[key] = [];
            byDay[key].push(task);
        }
        for (const key of Object.keys(byDay)) {
            byDay[key].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        }
        return { tasksByDay: byDay, tasksWithoutDate: withoutDate };
    }, [tasks]);

    // Células do grid: semanas completas (dom–sáb) cobrindo o mês corrente,
    // incluindo os dias vizinhos esmaecidos no início e no fim.
    const cells = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startOffset = firstDay.getDay();
        const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
        return Array.from({ length: totalCells }, (_, i) => {
            const date = new Date(year, month, i - startOffset + 1);
            return { date, inMonth: date.getMonth() === month };
        });
    }, [currentMonth]);

    const monthLabel = useMemo(() => {
        const label = currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }, [currentMonth]);

    const navigateMonth = (delta) => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const renderTaskChip = (task, { inPopover = false } = {}) => {
        const done = isTaskDone(task);
        const overdue = isOverdue(task);
        const time = formatTime(task.due_date);
        return (
            <button
                key={task._id}
                type="button"
                onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                title={task.title}
                className={`${D.chip} ${inPopover ? D.chipInPopover : ''} ${overdue ? D.chipOverdue : done ? D.chipDone : D.chipDefault}`}
            >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: overdue ? '#ef4444' : stageColor(task) }} />
                {time && <span className="tabular-nums flex-shrink-0 font-semibold">{time}</span>}
                <span className={`truncate ${done ? 'line-through' : ''}`}>{task.title || 'Sem título'}</span>
            </button>
        );
    };

    return (
        <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            {/* Toolbar */}
            <div className={D.toolbar}>
                <div className="flex items-center gap-2">
                    <div className="flex items-center bg-gray-50 dark:bg-[#1A1D21] rounded-lg border border-gray-200 dark:border-[#383838] p-0.5">
                        <button type="button" onClick={() => navigateMonth(-1)} className={D.navButton} title="Mês anterior" aria-label="Mês anterior">
                            <Iconify icon="eva:chevron-left-outline" width={18} />
                        </button>
                        <button type="button" onClick={() => navigateMonth(1)} className={D.navButton} title="Próximo mês" aria-label="Próximo mês">
                            <Iconify icon="eva:chevron-right-outline" width={18} />
                        </button>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs rounded-lg border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
                        onClick={() => setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
                    >
                        Hoje
                    </Button>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3] ml-1">{monthLabel}</h3>
                </div>

                {tasksWithoutDate.length > 0 && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <button type="button" className={D.noDateTrigger}>
                                <Iconify icon="eva:calendar-outline" width={14} />
                                {tasksWithoutDate.length} sem prazo
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 p-2 rounded-xl dark:bg-[#222529] dark:border-[#383838]">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD] px-1 pb-2">
                                Tarefas sem prazo
                            </p>
                            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                                {tasksWithoutDate.map(task => renderTaskChip(task, { inPopover: true }))}
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>

            {/* Weekday header */}
            <div className="grid grid-cols-7 border-b border-gray-100 dark:border-[#383838]">
                {WEEKDAY_LABELS.map(label => (
                    <div key={label} className={D.weekday}>{label}</div>
                ))}
            </div>

            {/* Month grid */}
            <div className="grid grid-cols-7">
                {cells.map(({ date, inMonth }) => {
                    const key = dateKey(date);
                    const dayTasks = tasksByDay[key] || [];
                    const visible = dayTasks.slice(0, MAX_VISIBLE_PER_DAY);
                    const hiddenCount = dayTasks.length - visible.length;
                    const isToday = isSameDay(date, today);
                    return (
                        <div
                            key={key}
                            onClick={() => onNewTask(date)}
                            className={`${D.dayCell} ${inMonth ? '' : D.dayCellOutside}`}
                            role="button"
                            title="Criar tarefa neste dia"
                        >
                            <div className="flex items-center justify-between">
                                <span className={`${D.dayNumber} ${isToday ? D.dayNumberToday : inMonth ? 'text-gray-700 dark:text-[#D1D2D3]' : 'text-gray-400 dark:text-gray-600'}`}>
                                    {date.getDate()}
                                </span>
                                {/* Atalho visível só no hover para deixar claro que o dia é clicável */}
                                <span className={D.dayAddHint}>
                                    <Iconify icon="eva:plus-outline" width={12} />
                                </span>
                            </div>
                            <div className="flex flex-col gap-0.5 mt-1 min-w-0">
                                {visible.map(task => renderTaskChip(task))}
                                {hiddenCount > 0 && (
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button type="button" onClick={e => e.stopPropagation()} className={D.moreButton}>
                                                +{hiddenCount} mais
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent align="start" className="w-72 p-2 rounded-xl dark:bg-[#222529] dark:border-[#383838]" onClick={e => e.stopPropagation()}>
                                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD] px-1 pb-2">
                                                {date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}
                                            </p>
                                            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                                                {dayTasks.map(task => renderTaskChip(task, { inPopover: true }))}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
